/**
 * 261104 E1-P2 Bước 3 — màn «Bàn giao vào Chapters»: done-pushed · done-conflict (đối chiếu từng tập: Giữ / Thay; loạt: Giữ tất cả / Thay tất cả trừ chương có Plan; STALE_CONFLICT ⇒ tải lại đối chiếu)
 * · done-push-error · done-empty. Chỉ hiển thị; ghi qua props. Chữ/nút do `doneCopyOf` / `compareCopyOf` (util thuần, có test). Không import antd.
 */
import React, { useCallback, useState } from 'react';
import type { SkillComparison, SkillConflictDecision, SkillConflictReplaceAll, SkillFinalState, SkillRunProjection } from '../../../types/skillAdaptation';
import { Alert } from './SkillParts';
import {
  compareCopyOf, conflictErrorOf, conflictRowsOf, doneCopyOf, keepBodyOf, replaceAllPlanOf, replaceBodyOf, resolveNoticeOf,
} from './skillWritingUtils';

/** `skipped` = DANH SÁCH số tập BE bỏ qua (đo thật ở smoke 05/11: `"skipped":[3]`), không phải số đếm. */
export interface ResolveResult { done: boolean; replaced: number; kept: number; skipped: number[]; remaining: number }
export interface DoneScreenProps {
  run: SkillRunProjection; stateId: string; busy: boolean;
  loadComparison: (n: number) => Promise<SkillComparison>;
  loadFinalState: () => Promise<SkillFinalState>;
  resolve: (body: { decisions?: SkillConflictDecision[]; keepAll?: boolean; replaceAll?: SkillConflictReplaceAll[] }) => Promise<ResolveResult>;
  /** Sau khi ghi quyết định thành công hoặc khi cần tải lại bản chiếu của lượt. */
  onChanged: () => void;
  onOpenChapters: () => void; onResumePush: () => void; onBackToWriting: () => void; onViewDecisions: () => void;
}

/** Màn đối chiếu hai cột cho MỘT tập xung đột. */
export const ConflictCompare: React.FC<{ n: number; load: DoneScreenProps['loadComparison']; resolve: DoneScreenProps['resolve']; busy: boolean; onChanged: () => void; onClose: () => void }> = ({ n, load, resolve, busy, onChanged, onClose }) => {
  const [cmp, setCmp] = useState<SkillComparison | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [working, setWorking] = useState(false);
  /** `keepError`: tải lại sau STALE_CONFLICT — người dùng vẫn phải thấy câu «Chương vừa thay đổi…» bên cạnh bản đối chiếu mới. */
  const fetchIt = useCallback(async (keepError = false): Promise<void> => {
    if (!keepError) setError(null);
    try { setCmp(await load(n)); } catch (e) { setCmp(null); setError(conflictErrorOf(e).text); }
  }, [load, n]);
  React.useEffect(() => { void fetchIt(); }, [fetchIt]);
  const copy = cmp ? compareCopyOf(cmp) : null;
  const submit = async (body: ReturnType<typeof keepBodyOf> | NonNullable<ReturnType<typeof replaceBodyOf>>): Promise<void> => {
    if (working) return;
    setWorking(true); setError(null);
    try { await resolve(body); onChanged(); onClose(); }
    catch (e) { const r = conflictErrorOf(e); setError(r.text); if (r.reload) await fetchIt(true); onChanged(); }   // STALE_CONFLICT ⇒ tải lại đối chiếu + bản chiếu lượt
    finally { setWorking(false); }
  };
  const replaceBody = cmp ? replaceBodyOf(cmp) : null;
  return (
    <div className="panel" data-testid="compare">
      <div className="row between dochead"><div><span className="eyebrow">{copy?.kindLabel ?? 'Đối chiếu'}</span><h2>{copy?.title ?? `Đối chiếu kịch bản tập ${n}`}</h2></div><button type="button" className="btn" data-testid="compare-close" onClick={onClose}>Về kết quả</button></div>
      {error ? <Alert kind="bad">{error}</Alert> : null}
      {!cmp && !error ? <p className="muted">Đang mở đối chiếu…</p> : null}
      {copy && cmp ? (
        <>
          <div className="fields">
            <div className="sourcebox" data-testid="compare-existing"><b>{copy.existingTitle}</b>{copy.existingContent === null ? <p className="muted">Không còn chương hiện có.</p> : <p style={{ whiteSpace: 'pre-wrap' }}>{copy.existingContent}</p>}</div>
            <div className="sourcebox" data-testid="compare-mine"><b>{copy.mineTitle}</b><p style={{ whiteSpace: 'pre-wrap' }}>{copy.mineContent}</p></div>
          </div>
          {copy.replaceNote ? <p className="note" data-testid="compare-note">{copy.replaceNote}</p> : null}
          <div className="row" style={{ marginTop: 12 }}>
            <button type="button" className="btn primary" data-testid="compare-keep" disabled={busy || working} onClick={() => void submit(keepBodyOf(n))}>{copy.keepLabel}</button>
            {copy.replaceDisabled || !replaceBody ? null : <button type="button" className="btn" data-testid="compare-replace" disabled={busy || working} onClick={() => void submit(replaceBody)}>{copy.replaceLabel}</button>}
          </div>
        </>
      ) : null}
    </div>
  );
};

export const SkillDoneScreen: React.FC<DoneScreenProps> = ({ run, stateId, busy, loadComparison, loadFinalState, resolve, onChanged, onOpenChapters, onResumePush, onBackToWriting, onViewDecisions }) => {
  const copy = doneCopyOf(run, stateId);
  const rows = stateId === 'done-conflict' ? conflictRowsOf(run.writing) : [];
  const [cmpN, setCmpN] = useState<number | null>(null);
  const [notice, setNotice] = useState<{ kind: 'bad' | 'ok'; text: string } | null>(null);
  const [final, setFinal] = useState<SkillFinalState | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  const primary = (): void => {
    if (copy.primary.kind === 'open-chapters') onOpenChapters();
    else if (copy.primary.kind === 'compare-first') setCmpN(rows[0]?.n ?? null);
    else if (copy.primary.kind === 'resume-push') onResumePush();
    else onBackToWriting();
  };
  const bulk = async (kind: 'keep' | 'replace'): Promise<void> => {
    if (bulkBusy) return;
    setBulkBusy(true); setNotice(null);
    try {
      if (kind === 'keep') { setNotice({ kind: 'ok', text: resolveNoticeOf(await resolve({ keepAll: true })) }); }
      else {
        const cmps = await Promise.all(rows.filter((r) => r.canReplace).map((r) => loadComparison(r.n)));
        const plan = replaceAllPlanOf(cmps);
        if (!plan.items.length) setNotice({ kind: 'bad', text: 'Không có chương nào thay được: các chương còn lại đã có Film Plan nên chỉ có thể giữ.' });
        else setNotice({ kind: 'ok', text: resolveNoticeOf(await resolve({ replaceAll: plan.items })) });
      }
    } catch (e) { setNotice({ kind: 'bad', text: conflictErrorOf(e).text }); }   // STALE_CONFLICT ⇒ chương đã đổi: tải lại bản chiếu để người dùng xem lại từng tập
    finally { setBulkBusy(false); onChanged(); }
  };
  const showFinal = async (): Promise<void> => {
    setNotice(null);
    try { setFinal(await loadFinalState()); } catch { setNotice({ kind: 'bad', text: 'Chưa mở được trạng thái cuối. Hãy thử lại.' }); }
  };

  if (cmpN !== null && stateId === 'done-conflict') {
    return <ConflictCompare key={cmpN} n={cmpN} load={loadComparison} resolve={resolve} busy={busy} onChanged={onChanged} onClose={() => setCmpN(null)} />;
  }
  return (
    <>
      <div className="panel run" data-testid={`done-panel-${stateId}`}>
        <div className="tick" data-testid="done-tick">{copy.tick}</div>
        <h2 data-testid="done-headline">{copy.headline}</h2>
        <p className="muted" data-testid="done-body">{copy.body}</p>
        {copy.detail ? <details className="fd"><summary>Xem chi tiết</summary><p className="muted">{copy.detail}</p></details> : null}
        {notice ? <Alert kind={notice.kind}>{notice.text}</Alert> : null}
        {rows.length ? (
          <div className="conflicts" data-testid="conflict-rows">
            {rows.map((r) => (
              <div className="runitem" key={r.n} data-testid={`conflict-row-${r.n}`}>
                <b>Tập {r.n}</b><span className="muted">{r.title}</span><span className="chip warn">{r.kindLabel}</span>{r.hasPlan ? <span className="note" data-testid={`conflict-keeponly-${r.n}`}>Chỉ có thể giữ</span> : null}
                <button type="button" className="btn sm" data-testid={`conflict-compare-${r.n}`} disabled={busy || bulkBusy} onClick={() => setCmpN(r.n)}>{r.compareLabel}</button>
              </div>
            ))}
          </div>
        ) : null}
        <div className="row" style={{ justifyContent: 'center', marginTop: 20 }}>
          <button type="button" className="btn primary" data-testid="done-primary" disabled={busy || bulkBusy || copy.primary.disabled} onClick={primary}>{copy.primary.label}</button>
          {copy.secondary.map((s) => <button key={s.kind} type="button" className="btn" data-testid={`done-${s.kind}`} disabled={busy} onClick={s.kind === 'decisions' ? onViewDecisions : () => void showFinal()}>{s.label}</button>)}
        </div>
        {copy.bulk ? (
          <div className="row" style={{ justifyContent: 'center', marginTop: 12 }}>
            <button type="button" className="btn" data-testid="bulk-keep" disabled={busy || bulkBusy} onClick={() => void bulk('keep')}>{copy.bulk.keepAll.label}</button>
            {copy.bulk.replaceAll ? <button type="button" className="btn" data-testid="bulk-replace" disabled={busy || bulkBusy} onClick={() => void bulk('replace')}>{copy.bulk.replaceAll.label}</button> : null}
          </div>
        ) : null}
        {copy.note ? <p className="note">{copy.note}</p> : null}
      </div>
      {final ? (
        <div className="panel soft" data-testid="final-state">
          <div className="row between dochead"><div><span className="eyebrow">Trạng thái cuối</span><h3>Sau tập {final.lastEpisodeN}</h3></div><button type="button" className="btn" data-testid="final-close" onClick={() => setFinal(null)}>Đóng</button></div>
          <p style={{ whiteSpace: 'pre-wrap' }}>{final.text}</p>
        </div>
      ) : null}
    </>
  );
};
